interface MultipartUploadParams {
    chunks: Array<{
        signedUrl: string;
        data: Blob;
    }>;
    onProgress?: (progress: number) => void;
}

// Calculates the number of parts needed for a multipart upload
// based on the size of the file and the chunk size
export function getNumberOfChunks(data: File | object, chunkSize: number): number {
    let size: number;

    if (data instanceof File) {
        size = data.size;
    } else {
        const blob = new Blob([JSON.stringify(data)], { type: "application/json" });
        size = blob.size;
    }

    return Math.ceil(size / chunkSize);
}

// Returns a chunk of the file or object data
// based on the specified chunk size and index
export function getFileChunk(data: File | object, chunkSize: number, chunkIndex: number): Blob {
    let blob: Blob;

    if (data instanceof File) {
        blob = data;
    } else {
        blob = new Blob([JSON.stringify(data)], { type: "application/json" });
    }

    const start = chunkIndex * chunkSize;
    const end = Math.min(start + chunkSize, blob.size);
    return blob.slice(start, end);
}

// Generates an array of chunks for multipart upload.
// Assigns a signed URL to each chunk using the provided getSignedUrl function
export function getMultipartChunks({
    data,
    chunkSize,
    getSignedUrl,
}: {
    data: File | object;
    chunkSize: number;
    getSignedUrl: (index: number) => string;
}): MultipartUploadParams["chunks"] {
    const chunks: MultipartUploadParams["chunks"] = [];
    const totalChunks = getNumberOfChunks(data, chunkSize);

    for (let i = 0; i < totalChunks; i++) {
        const blob = getFileChunk(data, chunkSize, i);
        const signedUrl = getSignedUrl(i);
        chunks.push({ signedUrl, data: blob });
    }

    return chunks;
}

// Uploads the chunks to S3 in parallel.
// Each chunk is uploaded using its corresponding signed URL.
// Returns an array of ETags and part numbers for each successfully uploaded chunk.
export async function multipartUploadToS3({ chunks, onProgress }: MultipartUploadParams): Promise<
    Array<{
        ETag: string;
        PartNumber: number;
    }>
> {
    const uploadPromises = chunks.map(({ signedUrl, data }, index) => {
        return fetch(signedUrl, {
            method: "PUT",
            body: data,
            headers: {
                "Content-Type": data instanceof File ? data.type : "application/json",
            },
        }).then((response) => {
            // Etag identifies the successfully uploaded part
            const etag = JSON.parse(response.headers.get("ETag") || "");

            if (response.ok && etag) {
                if (onProgress) {
                    onProgress(((index + 1) / chunks.length) * 100);
                }

                // Shape of the object expected by S3 to identify each part
                return {
                    ETag: etag,
                    PartNumber: index + 1,
                };
            } else {
                throw new Error(`Failed to upload chunk ${index}: ${response.statusText}`);
            }
        });
    });

    const results = await Promise.all(uploadPromises);
    return results;
}
